import React, { useMemo, useState, useRef, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  ColDef,
  ModuleRegistry,
  AllCommunityModule,
} from 'ag-grid-community';

import { InputSwitch } from 'primereact/inputswitch';
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import apiAxios, { apiWithRetry } from '../../utils/api';
import SuspenseLoader from '../../components/SuspenseLoader/SuspenseLoader';

import ExcelIcon from '../../assets/icons/excel.svg';
import SaveIcon from '../../assets/icons/save.svg';
import CommentIcon from '../../assets/icons/comment.svg';

import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

import ExcelCommentBox, { ExcelComment } from '../../components/ExcelComment/ExcelCommentBox';
import './AGGrid.scss';

ModuleRegistry.registerModules([AllCommunityModule]);

const months = [
  'title',
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  'Total'
];
const quarters = ['title', 'Q1', 'Q2', 'Q3', 'Q4', 'Total'];

const monthMap: Record<string, number> = {
  Jan: 1,
  Feb: 2,
  Mar: 3,
  Apr: 4,
  May: 5,
  Jun: 6,
  Jul: 7,
  Aug: 8,
  Sep: 9,
  Oct: 10,
  Nov: 11,
  Dec: 12
};

const cleanCurrency = (val: any) => Number(String(val).replace(/[$,%]/g, '').replace(/,/g, '')) || 0;

interface AGGridProps {
  filterResult: {
    year: number | null;
    customer: number | null;
    category: number | null;
    brand: number | null;
    isSummaryView: boolean;
  } | null;
}

const AGGrid: React.FC<AGGridProps> = ({ filterResult }) => {
  const gridRef = useRef<any>(null);

  const [isQuaterly, setIsQuaterly] = useState(false);
  const [comments, setComments] = useState<Record<string, ExcelComment>>({});
  const [activeCell, setActiveCell] = useState<any>(null);
  const [commentPosition, setCommentPosition] = useState<any>(null);
  const [showCommentBox, setShowCommentBox] = useState(false);
  const [menuPosition, setMenuPosition] = useState<any>(null);
  const [showMenu, setShowMenu] = useState(false);
  const [gridData, setGridData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [pendingInputs, setPendingInputs] = useState<
    Record<string, { value?: number; comment?: string }>
  >({});
  const hasChanges = Object.keys(pendingInputs).length > 0;
  const activeColumns = isQuaterly ? quarters : months;
  const currentUser = localStorage.getItem('username');
  const initials = currentUser
    ?.split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase();

  useEffect(() => {
    if (!filterResult) return;

    const fetchGridData = async () => {
      try {
        setLoading(true);

        const payload = {
          ...filterResult,
          viewType: isQuaterly ? 'Q' : 'M'
        };

        const res = await apiWithRetry(() => apiAxios.get('/fec/', { params: payload }));
        if (res?.data?.success) {
          setGridData(res.data.data);
        }
      } catch (err) {
        console.error('Grid API error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchGridData();
  }, [filterResult, isQuaterly]);

  useEffect(() => {
    if (!showMenu) return;

    const handleOutsideClick = () => {
      setShowMenu(false);
    };

    document.addEventListener('pointerdown', handleOutsideClick);

    return () => {
      document.removeEventListener('pointerdown', handleOutsideClick);
    };
  }, [showMenu]);

  /* ---------------- BUILD GRID DATA ---------------- */

  const { rows: rowData, extractedComments } = useMemo(() => {
    if (!gridData || typeof gridData !== 'object') {
      return { rows: [], extractedComments: {} };
    }

    const rows: any[] = [];
    const apiComments: Record<string, ExcelComment> = {};
    let idCounter = 0;

    const createRowId = () => `row-${idCounter++}`;

    Object.entries(gridData).forEach(([sectionKey, sectionRows]: any) => {
      if (!Array.isArray(sectionRows)) return;

      sectionRows.forEach((r: any) => {
        const editableMap: Record<string, boolean> = {};

        const values: Record<string, any> = {};

        r.periods.forEach((p: any) => {
          editableMap[p.label] = p.isEditable === true;
          values[p.label] = cleanCurrency(p.value);

          // hydrate existing comments
          if (p.hasComment && p.comment?.comment) {
            const monthLabel = p.label;
            const cellId = `${r.driverId || r.id}-${monthLabel}`;

            apiComments[cellId] = {
              cellId,
              text: p.comment.comment,
              user: p.comment.userName,
              timestamp: new Date(p.comment.timestamp).getTime()
            };
          }
        });

        rows.push({
          rowId: createRowId(),
          title: r.title,
          driverId: r.driverId || r.id,
          ...values,
          Total: cleanCurrency(r.total),
          editableMap,
          section: sectionKey
        });
      });

      rows.push({
        rowId: createRowId(),
        title: '',
        section: 'spacer'
      });
    });
    return { rows, extractedComments: apiComments };
  }, [gridData]);

  useEffect(() => {
    if (!extractedComments) return;

    setComments(prev => ({
      ...extractedComments,
      ...prev
    }));
  }, [extractedComments]);

  /* ---------------- TOTAL RECALCULATION ---------------- */

  const recalcTotals = (api: any) => {
    const rows: any[] = [];
    api.forEachNode((n: any) => rows.push(n.data));

    rows.forEach((row, idx) => {
      if (!row.title?.includes('Total')) return;

      const totals = Array(activeColumns.length - 2).fill(0);

      for (let i = idx + 1; i < rows.length; i++) {
        if (rows[i].section === 'spacer') break;

        activeColumns.slice(1, -1).forEach((m, j) => {
          totals[j] += Number(rows[i][m]) || 0;
        });
      }

      api.applyTransaction({
        update: [{
          ...row,
          ...Object.fromEntries(
            activeColumns.slice(1, -1)
              .map((m, j) => [m, totals[j]])
          ),
          Total: totals.reduce((a, b) => a + b, 0)
        }]
      });
    });

    api.refreshCells({ force: true });
  };

  const onCellValueChanged = (e: any) => {
    const month = e.colDef.field;

    if (!month || month === 'title' || month === 'Total') return;

    const updatedRow = { ...e.data };

    // calculate row total (Jan → Dec)
    const rowTotal = activeColumns
      .slice(1, -1)
      .reduce((sum, m) => sum + (Number(updatedRow[m]) || 0), 0);

    updatedRow.Total = rowTotal;

    e.api.applyTransaction({
      update: [updatedRow]
    });

    // recalc section totals after row total changes
    recalcTotals(e.api);

    const cellId = `${e.data.driverId}-${month}`;

    setPendingInputs((prev) => ({
      ...prev,
      [cellId]: {
        ...prev[cellId],
        value: e.newValue
      }
    }));
  };

  /* ---------------- COLUMN DEFINITIONS ---------------- */

  const columnDefs: ColDef[] = activeColumns.map(m => ({
    field: m,

    editable: params => {
      if (isQuaterly) return false;
      const { field } = params.colDef;

      if (
        field === 'title'
        || field === 'Total'
        || params.data.section === 'spacer'
        || params.data.section?.includes('header')
      ) return false;

      return field ? params.data?.editableMap?.[field] === true : false;
    },

    valueParser: params => {
      if (!params.newValue) return 0;
      return cleanCurrency(params.newValue);
    },

    valueFormatter: params => {
      if (params.column.getColId() === 'title') return params.value;
      if (params.value == null || params.value === '') return '';
      return `$${params.value}`;
    },

    cellClass: p => {
      const title = p.data?.title;

      if (title?.includes('Consumption Drivers – Total')) {
        return 'excel-consumption-header';
      }

      if (title?.includes('Inventory Drivers – Total')) {
        return 'excel-inventory-header';
      }

      return p?.node?.rowIndex && p.node.rowIndex % 2
        ? 'excel-row-alt'
        : 'excel-row-light';
    },

    cellRenderer: (p: any) => {
      const cellId = `${p.data.driverId}-${p.column.getColId()}`;
      const hasComment = comments[cellId];

      return (
        <div className="cell-wrap">
          {p.valueFormatted ?? p.value}

          {hasComment && !isQuaterly && (
            <img
              src={CommentIcon}
              alt="comment"
              className="excel-comment-icon"
              onClick={e => {
                e.stopPropagation();

                const rect = (e.target as HTMLElement).getBoundingClientRect();

                setCommentPosition({
                  top: rect.bottom + 8,
                  left: rect.left
                });

                setShowCommentBox(true);
                setActiveCell({
                  driverId: p.data.driverId,
                  col: p.column.getColId(),
                  readOnly: !!comments[cellId]
                });
              }}
            />
          )}
        </div>
      );
    }
  }));

  /* ---------------- COMMENT SAVE ---------------- */

  const handleSaveComment = (comment: ExcelComment) => {
    setComments(prev => ({
      ...prev,
      [comment.cellId]: comment
    }));

    setPendingInputs(prev => ({
      ...prev,
      [comment.cellId]: {
        ...prev[comment.cellId],
        comment: comment.text
      }
    }));

    gridRef.current?.api?.refreshCells({ force: true });

    setShowCommentBox(false);
  };

  const buildPayload = () => {
    if (!filterResult) return null;

    const api = gridRef.current?.api;
    if (!api) return null;

    const monthsPayload: Record<number, any[]> = {};

    Object.entries(pendingInputs).forEach(([cellId, data]) => {
      const [driverId, monthLabel] = cellId.split('-');

      const monthNum = monthMap[monthLabel];
      if (!monthNum) return;

      if (!monthsPayload[monthNum]) {
        monthsPayload[monthNum] = [];
      }

      const payloadItem: any = {
        driverCode: driverId,
        value: data.value ?? 0
      };

      if (data.comment) {
        payloadItem.comment = data.comment;
      }

      monthsPayload[monthNum].push(payloadItem);
    });

    return {
      year: filterResult.year,
      customerId: filterResult.customer,
      categoryId: filterResult.category,
      brandId: filterResult.brand,
      months: Object.entries(monthsPayload).map(([month, inputs]) => ({
        month: Number(month),
        inputs
      }))
    };
  };

  const handleSaveChanges = async () => {
    const payload = buildPayload();
    if (!payload) return;

    try {
      setLoading(true);

      await apiWithRetry(() => apiAxios.post('/fec/save-manual-input', payload));
      setPendingInputs({});
    } catch (err) {
      console.error('Save manual input failed', err);
    } finally {
      setLoading(false);
    }
  };

  const confirmSave = () => {
    confirmDialog({
      message: 'Are you sure you want to save the changes?',
      header: 'Confirm Save',
      icon: 'pi pi-exclamation-triangle',
      accept: handleSaveChanges,
      reject: () => { },
      acceptLabel: 'Yes',
      rejectLabel: 'No'
    });
  };

  /* ---------------- UI ---------------- */

  return (
    <>
      <ConfirmDialog />
      {loading && <SuspenseLoader />}

      <div className="excel">

        <div className="mx-3 my-2 flex align-items-center justify-content-between">
          <div className="view-toggle excel__header-toggle">
            <span className={isQuaterly ? 'inactive' : ''}>Monthly</span>

            <div className="custom-toggle-wrapper">
              <InputSwitch
                checked={isQuaterly}
                onChange={e => setIsQuaterly(e.value!)}
              />
            </div>

            <span className={!isQuaterly ? 'inactive' : ''}>Quarterly</span>
          </div>

          <div className="flex gap-2">
            <Button
              label="Save Changes"
              icon={<img src={SaveIcon} alt="save" className="btn-icon" />}
              className="excel__header-button p-button-outlined"
              onClick={confirmSave}
              disabled={!hasChanges}
            />

            <Button
              label="Export Excel"
              icon={<img src={ExcelIcon} alt="excel" className="btn-icon" />}
              className="excel__header-button--two p-button-outlined"
            />
          </div>
        </div>

        <div className="ag-theme-alpine" style={{ height: '100vh' }} onContextMenu={(e) => e.preventDefault()}>
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            columnDefs={columnDefs}
            onCellValueChanged={onCellValueChanged}
            getRowId={(params) => params.data.driverId}
            onCellContextMenu={(params) => {
              params?.event?.preventDefault();

              // disable everything in quarterly mode
              if (isQuaterly) return;

              const field = params.column.getColId();

              // allow comments only on editable cells
              if (
                field === 'title'
                || field === 'Total'
                || params.data.section === 'spacer'
                || !params.data?.editableMap?.[field]
              ) {
                return;
              }

              setActiveCell({
                driverId: params.data.driverId,
                col: params.column.getColId()
              });

              setMenuPosition({
                top: (params?.event as MouseEvent)?.clientY,
                left: (params?.event as MouseEvent)?.clientX
              });

              setShowMenu(true);
            }}
          />

        </div>

        {showCommentBox && activeCell && (
          <ExcelCommentBox
            cellId={`${activeCell.driverId}-${activeCell.col}`}
            user={initials}
            comment={comments[`${activeCell.driverId}-${activeCell.col}`]}
            readOnly={!!comments[`${activeCell.driverId}-${activeCell.col}`]}
            position={commentPosition}
            onSave={handleSaveComment}
            onClose={() => setShowCommentBox(false)}
          />
        )}

        {showMenu && (
          <div
            className="excel-context-menu"
            onPointerDown={(e) => e.stopPropagation()}
            style={{
              position: 'fixed',
              top: menuPosition.top,
              left: menuPosition.left,
              zIndex: 9999
            }}
          >
            <div
              className="menu-item"
              onClick={() => {
                setShowMenu(false);

                const rect = {
                  top: menuPosition.top,
                  left: menuPosition.left
                };

                setCommentPosition(rect);
                setShowCommentBox(true);
              }}
            >
              Add / Edit Comment
            </div>
          </div>
        )}

      </div>
    </>
  );
};

export default AGGrid;
